import { getVersion } from "@vericus/cadmus-editor-prosemirror";

import { ApolloError, FetchResult } from "@apollo/client";
import { createAsyncThunk, GetThunkAPI } from "@reduxjs/toolkit";

import { __GLOBAL_SESSION_ID } from "@/client/globals";
import { RootState } from "@/data/store";
import { selectAssessmentFormat } from "@/features/assignment";
import {
  SaveBlockSnapshotMutation,
  SaveMutation,
  SubmissionType,
  TaskFormat,
} from "@/generated/graphql";
import { isUnauthorizedError, matchGraphQLErrorCode } from "@/graphql/errors";
import { readLatestBlockSnapshot, readLatestSave } from "@/graphql/selectors";
import {
  ClassicEditorName,
  getTotalWordCount,
  selectNamedEditorStoreEditor,
} from "@/stores/editor-store";
import {
  serialiseSnapshot,
  serialiseSnapshotsAsSave,
  Snapshot,
} from "@/stores/snapshot";

import { onCloudSave, onSnapshot, onSubmit, onSubmitAttempt } from "./api";
import {
  selectAnswerSnapshot,
  selectIsSubmitting,
  selectSavingMechanism,
  selectSubmitDate,
  selectWorkId,
} from "./selectors";
import {
  PendingSnapshot,
  SaveError,
  SavingMechanism,
  SubmitError,
} from "./types";

export interface SaveBlockSnapshotPayload {
  /** The answer block id to save the snapshot to. */
  answerBlockId: string;
  /** Snapshot state */
  snapshot: Snapshot;
  /** Whether the action is triggered via an editor auto save. */
  isAutoSave: boolean;
}

/**
 * Perform GraphQL mutation to save a snapshot of the current state of the
 * document. It will send the following blocks:
 * - body
 * - notes
 * - references
 */
export const saveBlockSnapshot = createAsyncThunk<
  FetchResult<SaveBlockSnapshotMutation>,
  PendingSnapshot,
  { state: RootState; rejectValue: SaveError }
>("authority/snapshotBlock", async (payload, thunkAPI) => {
  const { snapshot, answerBlockId } = payload;
  const state = thunkAPI.getState();
  const assessmentId = state.authority.assessmentId;
  const workId = state.authority.workId;

  if (!workId || !assessmentId) {
    throw new Error("Student Work not loaded into Redux");
  }

  const currentBlockSnapshot = readLatestBlockSnapshot(answerBlockId);
  const previousSnapshotId = currentBlockSnapshot?.id ?? null;
  const content = JSON.stringify(serialiseSnapshot(snapshot));

  try {
    const result = await onSnapshot(
      workId,
      answerBlockId,
      content,
      snapshot.version,
      previousSnapshotId,
      snapshot.wordCountChange
    );
    return result;
  } catch (error) {
    // `already_reported` means the server already holds this version: a lost
    // ack (a timed-out save that actually committed) or another tab that saved
    // from the same base, since we have no tab syncing. The version is saved, so
    // resolve as fulfilled, the reducer acks the snapshot and clears the save
    // state, rather than leaving a stale error. We can't rebase, so a
    // conflicting tab's edits for that version are lost until rebasable steps.
    if (error instanceof ApolloError && isSnapshotAlreadyReportedError(error)) {
      return { data: null };
    }

    // Known Unauthorized error
    if (error instanceof ApolloError && isUnauthorizedError(error)) {
      return thunkAPI.rejectWithValue({
        kind: "unauthorized",
        title: "Authentication error",
        detail:
          "Your session has expired. Please refresh the page and try again.",
      });
    }

    // Other errors are re-thrown and will be mapped to a
    // saveBlockSnapshot.rejected action, in `action.error`.
    throw error;
  }
});

interface SaveFullPayload {
  /**
   * The classic editor snapshot to save.
   *
   * Should pass any of the editors that had changes and the new content will
   * override the latest snapshots
   */
  pendingSnapshot?: PendingSnapshot;
}

/**
 * Async Cloud Saving request action using the `Save` graphql mutation.
 */
export const saveFull = createAsyncThunk<
  FetchResult<SaveMutation>,
  SaveFullPayload | undefined,
  { state: RootState; rejectValue: SaveError }
>("authority/saveWork", async (payload, thunkAPI) => {
  // Read the latest save information from the graphql cache
  const latestSave = readLatestSave();

  const state = thunkAPI.getState();
  const workId = state.authority.workId;
  const assessmentId = state.authority.assessmentId;

  if (!workId || !assessmentId) {
    throw new Error("Student Work not loaded into Redux");
  }

  const metadata = {
    workId,
    assessmentId,
    sessionId: __GLOBAL_SESSION_ID.current,
    prevSaveId: latestSave?.serverId ?? null,
    prevVersionId: latestSave?.version ?? null,
  };

  const doc = serialiseSnapshotsAsSave(
    {
      body: prepareClassicSnapshot(
        ClassicEditorName.Body,
        state,
        payload?.pendingSnapshot
      ),
      references: prepareClassicSnapshot(
        ClassicEditorName.References,
        state,
        payload?.pendingSnapshot
      ),
      notes: prepareClassicSnapshot(
        ClassicEditorName.Notes,
        state,
        payload?.pendingSnapshot
      ),
    },
    metadata
  );

  const content = JSON.stringify(doc);
  return onCloudSave(workId, content, metadata);
});

// Pick the editors snapshot from:
//
//   1. The action payload, or
//   2. The authority slice state holding snapshots, or
//   3. The live editor instance in the answer store, or
//   4. Empty version 0 snapshot
//
function prepareClassicSnapshot(
  editorName: ClassicEditorName,
  state: RootState,
  pendingSnapshot?: PendingSnapshot
): Snapshot {
  // NOTE PendingSnapshots created from Editors registered in the EditorStore
  // use the store's key as the answerBlockId. This key in Classic Cadmus will
  // be the Classic Editor name as the editor was not sourced from an actual
  // AnswerBlock.
  if (pendingSnapshot?.answerBlockId === editorName) {
    return pendingSnapshot.snapshot;
  }
  const maybeSnapshot = selectAnswerSnapshot(state, editorName);
  if (maybeSnapshot) {
    return maybeSnapshot;
  }
  const editor = selectNamedEditorStoreEditor(editorName);
  const answerDoc = editor?.getJSON();
  const version = editor ? getVersion(editor.state) : 0;
  return {
    version,
    answerDoc,
  };
}

export interface SubmitPayload {
  /**
   * Select a submission type.
   * @default `SubmissionType.Final`
   */
  submissionType?: SubmissionType;
  /*
   * Client-side locked Submission date for this attempt.
   * @default `new Date()`.
   */
  submissionDate?: string;
  /**
   * Trigger was an auto-submission.
   * @default false.
   */
  autoSubmission?: boolean;
  /**
   * Retry attempt number.
   * @default 1.
   */
  attempt?: number;
}

/**
 * Submit the work.
 */
export const submit = createAsyncThunk<
  unknown,
  SubmitPayload,
  { state: RootState; rejectValue: SubmitError }
>(
  "authority/submit",
  async (payload, thunkAPI) => {
    // Depending on the saving mechanism, choose the appropriate submit action
    const state = thunkAPI.getState();
    const savingMechanism = selectSavingMechanism(state);
    const format = selectAssessmentFormat(state);

    const totalWC = getTotalWordCount();
    if (format === TaskFormat.Classic && totalWC < 20) {
      return thunkAPI.rejectWithValue({
        kind: "validation",
        title: "Incomplete work",
        detail: "You must have 20 words or more to submit.",
      });
    }

    if (savingMechanism === SavingMechanism.BLOCK) {
      return submitAttempt(payload, state, thunkAPI);
    }

    /*
    // It's an older way to check for pending saves before submitting,
    // now all the saving is done by snapshotQueueListener and we need to find a way synchronously flush the queue before submission
    const pendingSave = hasPendingSave();
    const saveError = selectSaveError(thunkAPI.getState());
    if (pendingSave || saveError) {
      await thunkAPI.dispatch(saveFull({ isAutoSave: false }));
    }
    */

    // Read the latest save information from the graphql cache
    const workId = selectWorkId(state);
    const latestSave = readLatestSave();
    const saveId = latestSave?.serverId;

    if (!saveId || !workId) {
      return thunkAPI.rejectWithValue({
        kind: "validation",
        title: "Incomplete work",
      });
    }

    const submittedAt = payload?.submissionDate || selectSubmitDate(state);
    const submissionType = payload?.submissionType
      ? payload.submissionType
      : SubmissionType.Final;

    // Add a jitter for auto submissions
    if (payload?.autoSubmission) {
      await new Promise((r) => setTimeout(r, Math.random() * 3000));
    }

    try {
      return await onSubmit(workId, saveId, submissionType, submittedAt);
    } catch (error) {
      if (error instanceof ApolloError) {
        if (isAlreadySubmitted(error)) {
          return thunkAPI.rejectWithValue({
            kind: "validation",
            title: "Submission error",
            detail: "Submission with newer save exists",
          });
        } else if (isValidationError(error)) {
          return thunkAPI.rejectWithValue({
            kind: "validation",
            title: "Submission error",
            detail: "We couldn't process this request. Please, try again later",
          });
        }
      }
      throw error;
    }
  },
  {
    condition: (_arg, { getState }) => {
      const state = getState();
      // Don't fire another submission attempt, if another is in progress
      if (selectIsSubmitting(state)) {
        return false;
      }
      return true;
    },
  }
);

/**
 * Make a submission attempt targetting the known next attempt number for the
 * Work.
 *
 * The latest Work contents seen by the server will be submitted, or the
 * submission will be rejected if the attempt has already been made by another
 * client. Such rejections should stop the submission process on the current
 * client.
 */
export async function submitAttempt(
  payload: SubmitPayload,
  state: RootState,
  thunkAPI: GetThunkAPI<{
    state: RootState;
    rejectValue: SubmitError;
  }>
) {
  const workId = selectWorkId(state);
  const nextAttemptNumber = state.authority.submitAttemptCount;

  if (typeof nextAttemptNumber !== "number") {
    return thunkAPI.rejectWithValue({
      kind: "validation",
      title: "Unknown Submission Attempt",
    });
  }

  // Add a jitter for auto submissions
  if (payload?.autoSubmission) {
    await new Promise((r) => setTimeout(r, Math.random() * 3000));
  }

  try {
    const submissionType = payload?.submissionType ?? SubmissionType.Final;
    const submissionDate = payload.submissionDate ?? null;

    return await onSubmitAttempt({
      workId,
      attemptNumber: nextAttemptNumber,
      type: submissionType,
      submissionDate,
    });
  } catch (error) {
    if (error instanceof ApolloError) {
      if (isConflictError(error)) {
        return thunkAPI.rejectWithValue({
          kind: "already_reported",
          title: "Submission attempt has already been made",
        });
      }
      if (isEmptyError(error)) {
        return thunkAPI.rejectWithValue({
          kind: "validation",
          title: "Incomplete Work",
          detail:
            "There is not enough words to submit. Please try again later.",
        });
      }
      if (isValidationError(error)) {
        return thunkAPI.rejectWithValue({
          kind: "validation",
          title: "Submission error",
        });
      }
    }
    throw error;
  }
}

/// Snapshot errors

function isSnapshotAlreadyReportedError(error: ApolloError) {
  return matchGraphQLErrorCode(error, ["already_reported"]);
}

/// Submission errors

function isValidationError(error: ApolloError) {
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0] &&
    // in case of validation error pantheon returns formatted error with code field
    "code" in error.graphQLErrors[0] &&
    error.graphQLErrors[0].code === "VALIDATION_ERROR"
  );
}

function isAlreadySubmitted(error: ApolloError) {
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0] &&
    "reason" in error.graphQLErrors[0] &&
    typeof error.graphQLErrors[0].reason === "string" &&
    ["newer_exist", "conflict"].includes(error.graphQLErrors[0].reason)
  );
}

function isEmptyError(error: ApolloError) {
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0] &&
    "code" in error.graphQLErrors[0] &&
    error.graphQLErrors[0].code === "empty_work"
  );
}

function isConflictError(error: ApolloError) {
  return (
    error.graphQLErrors.length > 0 &&
    error.graphQLErrors[0] &&
    "code" in error.graphQLErrors[0] &&
    error.graphQLErrors[0].code === "conflict"
  );
}
